/**
 * CITO Selection — Content Domain
 *
 * Intelligent question selection for CITO assessments.
 * Uses tier-based difficulty distribution for balanced test composition.
 *
 * Created in R38 (DDD Fase 7 — questionLoader god-file split).
 */

import { QuestionCore } from '../types'
import { createRng } from '../../utils/deterministicRng'
import { error as logError } from '../../utils/logger'
import { CITO_ELIGIBLE_QUESTIONS } from './questionLoader'
import { isCitoEligible } from './questionValidation'
import { fisherYatesShuffle, shuffleQuestionOptions } from './questionShuffling'

/**
 * Get questions for CITO (with intelligent selection).
 *
 * Uses tier-based difficulty distribution for balanced test composition.
 *
 * @param count - Number of questions to select (default: 30)
 * @param seed - Optional seed for deterministic selection (for reproducibility)
 */
export function getCitoQuestions(
  count: number = 30,
  seed?: number,
): QuestionCore[] {
  try {
    const eligible = CITO_ELIGIBLE_QUESTIONS.filter(isCitoEligible)
    if (eligible.length === 0) {
      logError('CITO: No eligible questions found')
      return []
    }

    // Create RNG (deterministic if seed provided, random otherwise)
    const rng = seed !== undefined ? createRng(seed) : null
    const randomFn = () => (rng ? rng.random() : Math.random())

    // Group by skill
    const bySkill = new Map<string, QuestionCore[]>()
    eligible.forEach((q) => {
      const skill = q.skill
      if (!bySkill.has(skill)) bySkill.set(skill, [])
      bySkill.get(skill)!.push(q)
    })

    // Within each skill, pick max 1 per familyId
    const selectedBySkill = new Map<string, QuestionCore[]>()
    bySkill.forEach((questions, skill) => {
      const byFamily = new Map<string, QuestionCore[]>()
      questions.forEach((q) => {
        if (!byFamily.has(q.familyId)) byFamily.set(q.familyId, [])
        byFamily.get(q.familyId)!.push(q)
      })
      const skillQuestions: QuestionCore[] = []
      byFamily.forEach((familyQuestions) => {
        const randomIndex = Math.floor(randomFn() * familyQuestions.length)
        skillQuestions.push(familyQuestions[randomIndex])
      })
      selectedBySkill.set(skill, skillQuestions)
    })

    // Proportional allocation per skill
    const totalAvailable = Array.from(selectedBySkill.values()).reduce(
      (sum, questions) => sum + questions.length,
      0,
    )
    const questionsPerSkill = new Map<string, number>()
    selectedBySkill.forEach((questions, skill) => {
      const proportion = questions.length / totalAvailable
      questionsPerSkill.set(skill, Math.max(1, Math.round(count * proportion)))
    })

    // Select with tier-based difficulty balance
    const selected: QuestionCore[] = []
    const shuffleArray = <T,>(arr: T[]): T[] =>
      fisherYatesShuffle(arr, randomFn)

    selectedBySkill.forEach((questions, skill) => {
      const targetCount = questionsPerSkill.get(skill) || 0

      // Use tier for difficulty categorization (1-6 easy, 7-12 medium, 13-16 hard)
      const easyQuestions = questions.filter((q) => q.tier <= 6)
      const mediumQuestions = questions.filter(
        (q) => q.tier >= 7 && q.tier <= 12,
      )
      const hardQuestions = questions.filter((q) => q.tier >= 13)
      const easyCount = Math.round(targetCount * 0.4)
      const mediumCount = Math.round(targetCount * 0.4)
      const hardCount = targetCount - easyCount - mediumCount

      // Deterministic Fisher-Yates shuffle using seeded RNG when available
      const shuffledEasy = shuffleArray(easyQuestions)
      const shuffledMedium = shuffleArray(mediumQuestions)
      const shuffledHard = shuffleArray(hardQuestions)
      selected.push(...shuffledEasy.slice(0, easyCount))
      selected.push(...shuffledMedium.slice(0, mediumCount))
      selected.push(...shuffledHard.slice(0, hardCount))
      const remaining =
        targetCount -
        (Math.min(easyCount, shuffledEasy.length) +
          Math.min(mediumCount, shuffledMedium.length) +
          Math.min(hardCount, shuffledHard.length))
      if (remaining > 0) {
        const allShuffled = shuffleArray(
          questions.filter((q) => !selected.includes(q)),
        )
        selected.push(...allShuffled.slice(0, remaining))
      }
    })
    const finalSelection = shuffleArray(selected).slice(0, count)

    // Shuffle options for each question
    const shuffledQuestions = finalSelection.map((q) =>
      shuffleQuestionOptions(q, seed),
    )
    return shuffledQuestions
  } catch (error) {
    logError('Error in getCitoQuestions:', error)
    return []
  }
}

/**
 * Get CITO questions by skill.
 *
 * @param skill - Skill to filter by
 * @param count - Number of questions to select (default: 10)
 * @param seed - Optional seed for deterministic selection
 */
export function getCitoQuestionsBySkill(
  skill: string,
  count: number = 10,
  seed?: number,
): QuestionCore[] {
  try {
    const eligible = CITO_ELIGIBLE_QUESTIONS.filter(
      (q) => isCitoEligible(q) && q.skill === skill,
    )
    if (eligible.length === 0) return []

    // Create RNG (deterministic if seed provided, random otherwise)
    const rng = seed !== undefined ? createRng(seed) : null
    const randomFn = () => (rng ? rng.random() : Math.random())
    const byFamily = new Map<string, QuestionCore[]>()
    eligible.forEach((q) => {
      if (!byFamily.has(q.familyId)) byFamily.set(q.familyId, [])
      byFamily.get(q.familyId)!.push(q)
    })
    const uniqueQuestions: QuestionCore[] = []
    byFamily.forEach((familyQuestions) => {
      const randomIndex = Math.floor(randomFn() * familyQuestions.length)
      uniqueQuestions.push(familyQuestions[randomIndex])
    })

    // Tier-based difficulty balance
    const easyQuestions = uniqueQuestions.filter((q) => q.tier <= 6)
    const mediumQuestions = uniqueQuestions.filter(
      (q) => q.tier >= 7 && q.tier <= 12,
    )
    const hardQuestions = uniqueQuestions.filter((q) => q.tier >= 13)
    const easyCount = Math.round(count * 0.4)
    const mediumCount = Math.round(count * 0.4)
    const hardCount = count - easyCount - mediumCount

    // Deterministic Fisher-Yates shuffle using seeded RNG when available
    const shuffleArray = <T,>(arr: T[]): T[] =>
      fisherYatesShuffle(arr, randomFn)
    const selected: QuestionCore[] = []
    selected.push(...shuffleArray(easyQuestions).slice(0, easyCount))
    selected.push(...shuffleArray(mediumQuestions).slice(0, mediumCount))
    selected.push(...shuffleArray(hardQuestions).slice(0, hardCount))
    const remaining = count - selected.length
    if (remaining > 0) {
      const available = shuffleArray(
        uniqueQuestions.filter((q) => !selected.includes(q)),
      )
      selected.push(...available.slice(0, remaining))
    }
    return shuffleArray(selected).slice(0, count)
  } catch (error) {
    logError('Error in getCitoQuestionsBySkill:', error)
    return []
  }
}

/**
 * Get skill-based performance summary.
 */
export function getCitoSkillSummary(): {
  totalQuestions: number
  bySkill: Map<
    string,
    {
      total: number
      bySubSkill: Map<string, number>
      byTier: {
        easy: number
        medium: number
        hard: number
      }
    }
  >
} {
  const bySkill = new Map<
    string,
    {
      total: number
      bySubSkill: Map<string, number>
      byTier: {
        easy: number
        medium: number
        hard: number
      }
    }
  >()
  CITO_ELIGIBLE_QUESTIONS.forEach((q) => {
    const skill = q.skill
    if (!bySkill.has(skill)) {
      bySkill.set(skill, {
        total: 0,
        bySubSkill: new Map(),
        byTier: {
          easy: 0,
          medium: 0,
          hard: 0,
        },
      })
    }
    const skillData = bySkill.get(skill)!
    skillData.total++
    // Tier-based categorization
    if (q.tier <= 6) skillData.byTier.easy++
    else if (q.tier <= 12) skillData.byTier.medium++
    else skillData.byTier.hard++
    skillData.bySubSkill.set(
      q.subSkill,
      (skillData.bySubSkill.get(q.subSkill) || 0) + 1,
    )
  })
  return {
    totalQuestions: CITO_ELIGIBLE_QUESTIONS.length,
    bySkill,
  }
}
